import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { CheckCircle, AlertTriangle } from 'lucide-react';

export interface ItemGradingData {
  itemId: string;
  itemQualityId?: string; // existing procurement_item_quality id
  productName: string;
  quantity: number;
  preliminaryDamageKg: number;
  preliminaryCompensationKg: number;
  preliminaryDamagePercentage: number;
  qualityGrade?: string;
  isRejected: boolean;
}

export interface FinalGradingSubmitData {
  notes: string;
  items: Array<{
    itemId: string;
    itemQualityId?: string;
    damageKg: number;
    compensationKg: number;
    damagePercentage: number;
  }>;
  // Aggregated totals (computed)
  totalDamageKg: number;
  totalCompensationKg: number;
  avgDamagePercentage: number;
}

interface FinalGradingFormProps {
  items: ItemGradingData[];
  preliminaryData: {
    damageKg: number;
    compensationKg: number;
    damagePercentage: number;
  };
  totalQuantity: number;
  onSubmit: (data: FinalGradingSubmitData) => void;
  isSubmitting: boolean;
}

interface ItemFormState {
  damageKg: number;
  compensationKg: number;
  damagePercentage: number;
}

const FinalGradingForm: React.FC<FinalGradingFormProps> = ({
  items,
  preliminaryData,
  totalQuantity,
  onSubmit,
  isSubmitting
}) => {
  const [notes, setNotes] = useState('');
  
  // Initialize per-item state from preliminary values
  const [itemStates, setItemStates] = useState<Record<string, ItemFormState>>(() => {
    const initial: Record<string, ItemFormState> = {};
    items.forEach(item => {
      initial[item.itemId] = {
        damageKg: item.preliminaryDamageKg,
        compensationKg: item.preliminaryCompensationKg,
        damagePercentage: item.preliminaryDamagePercentage,
      };
    });
    return initial;
  });

  const updateItemField = (itemId: string, field: keyof ItemFormState, value: number) => {
    setItemStates(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], [field]: value }
    }));
  };

  // Aggregate totals
  const totals = useMemo(() => {
    let totalDamage = 0;
    let totalCompensation = 0;
    let totalQty = 0;

    items.forEach(item => {
      const state = itemStates[item.itemId];
      if (state) {
        totalDamage += state.damageKg;
        totalCompensation += state.compensationKg;
        totalQty += item.quantity;
      }
    });

    return {
      damageKg: totalDamage,
      compensationKg: totalCompensation,
      damagePercentage: totalQty > 0 ? (totalDamage / totalQty) * 100 : 0,
    };
  }, [itemStates, items]);

  const compensationDiff = totals.compensationKg - preliminaryData.compensationKg;
  const hasDifference = Math.abs(compensationDiff) > 0.01;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      notes,
      items: items.map(item => ({
        itemId: item.itemId,
        itemQualityId: item.itemQualityId,
        damageKg: itemStates[item.itemId]?.damageKg || 0,
        compensationKg: itemStates[item.itemId]?.compensationKg || 0,
        damagePercentage: itemStates[item.itemId]?.damagePercentage || 0,
      })),
      totalDamageKg: totals.damageKg,
      totalCompensationKg: totals.compensationKg,
      avgDamagePercentage: parseFloat(totals.damagePercentage.toFixed(2)),
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-primary" />
          Final Grading — Per Item Assessment
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Per-item inputs */}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead className="text-right">Qty (kg)</TableHead>
                  <TableHead className="text-right">Prelim. Damage</TableHead>
                  <TableHead className="text-right">Final Damage (kg)</TableHead>
                  <TableHead className="text-right">Prelim. Comp.</TableHead>
                  <TableHead className="text-right">Final Compensation (kg)</TableHead>
                  <TableHead className="text-right">Final Damage %</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map(item => {
                  const state = itemStates[item.itemId];
                  return (
                    <TableRow key={item.itemId}>
                      <TableCell>
                        <div className="font-medium">{item.productName}</div>
                        {item.qualityGrade && (
                          <Badge variant="outline" className="mt-1 text-xs">{item.qualityGrade}</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">{item.quantity}</TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {item.preliminaryDamageKg}
                      </TableCell>
                      <TableCell className="text-right">
                        <Input
                          type="number"
                          min="0"
                          step="0.1"
                          className="w-24 ml-auto text-right"
                          value={state?.damageKg ?? 0}
                          onChange={e => updateItemField(item.itemId, 'damageKg', parseFloat(e.target.value) || 0)}
                        />
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {item.preliminaryCompensationKg}
                      </TableCell>
                      <TableCell className="text-right">
                        <Input
                          type="number"
                          min="0"
                          step="0.1"
                          className="w-24 ml-auto text-right"
                          value={state?.compensationKg ?? 0}
                          onChange={e => updateItemField(item.itemId, 'compensationKg', parseFloat(e.target.value) || 0)}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          className="w-20 ml-auto text-right"
                          value={state?.damagePercentage ?? 0}
                          onChange={e => updateItemField(item.itemId, 'damagePercentage', parseFloat(e.target.value) || 0)}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Aggregate Summary */}
          <div className="bg-muted/50 border rounded-lg p-4">
            <h4 className="font-medium mb-3">📊 Aggregate Totals (auto-calculated)</h4>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Total Damage:</span>
                <p className="font-bold text-lg">{totals.damageKg.toFixed(1)} kg</p>
              </div>
              <div>
                <span className="text-muted-foreground">Total Compensation:</span>
                <p className="font-bold text-lg">{totals.compensationKg.toFixed(1)} kg</p>
              </div>
              <div>
                <span className="text-muted-foreground">Avg Damage %:</span>
                <p className="font-bold text-lg">{totals.damagePercentage.toFixed(1)}%</p>
              </div>
            </div>
          </div>

          {/* Difference from Preliminary */}
          {hasDifference && (
            <div className={`p-4 rounded-lg border ${compensationDiff > 0 ? 'bg-destructive/10 border-destructive/30' : 'bg-primary/10 border-primary/30'}`}>
              <div className={`flex items-center gap-2 ${compensationDiff > 0 ? 'text-destructive' : 'text-primary'}`}>
                <AlertTriangle className="h-5 w-5" />
                <span className="font-medium">Difference from Preliminary</span>
              </div>
              <div className="mt-2 grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="font-medium">Preliminary:</span> {preliminaryData.compensationKg} kg
                </div>
                <div>
                  <span className="font-medium">Final:</span> {totals.compensationKg.toFixed(1)} kg
                </div>
                <div>
                  <span className="font-medium">Difference:</span> {compensationDiff > 0 ? '+' : ''}{compensationDiff.toFixed(1)} kg
                </div>
              </div>
            </div>
          )}

          {/* Net Payable */}
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
            <h4 className="font-medium mb-2">Net Payable Calculation</h4>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Total Quantity:</span>
                <p className="font-bold">{totalQuantity} kg</p>
              </div>
              <div>
                <span className="text-muted-foreground">Compensation:</span>
                <p className="font-bold">- {totals.compensationKg.toFixed(1)} kg</p>
              </div>
              <div>
                <span className="text-muted-foreground">Net Payable:</span>
                <p className="font-bold">{(totalQuantity - totals.compensationKg).toFixed(1)} kg</p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Final Grading Notes</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add notes about the final grading assessment..."
              rows={3}
            />
          </div>

          <Button 
            type="submit" 
            className="w-full"
            disabled={isSubmitting}
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            {isSubmitting ? 'Submitting...' : 'Complete Final Grading'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default FinalGradingForm;
